import styled from '@emotion/styled';
import * as React from 'react';

import { mq } from '@/utility/mediaQuery';
import { defaultTheme } from '@/utility/theme';
import { getBicycleColor, getTextColor } from '@/utility/theme/bicycleColor';

interface BetCheckBoxProps {
  checked?: boolean;
  disabled?: boolean;
  value?: string | number;
  num: number;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export const NumberBox = styled.div(
  (props: { checked?: boolean; disabled?: boolean; num: number }) =>
    mq({
      position: 'relative',
      width: ['35px', '35px', '40px', '40px'],
      height: ['35px', '35px', '40px', '40px'],
      border: props.num === 1 && props.checked ? `1px solid #A8A8A8` : 'none',
      borderRadius: defaultTheme.border.radius.s,
      backgroundColor: props.checked
        ? getBicycleColor(props.num)
        : defaultTheme.background.mediumLightGray,
      cursor: props.disabled ? 'inherit' : 'pointer',
      fontFamily: defaultTheme.font.family.bicycleNumber,
      fontWeight: 'bold',
      lineHeight: 1,
      margin: '0 auto',
      '&::before': {
        content: `"${props.num}"`,
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        fontSize: defaultTheme.font.size.xl,
        color: props.checked
          ? getTextColor(props.num)
          : defaultTheme.text.white,
      },
      '&::after': {
        content: "''",
        display: props.disabled ? 'inherit' : 'none',
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%) rotate(-45deg)',
        width: '50px',
        height: '6px',
        border: `1.5px solid ${defaultTheme.color.white}`,
        backgroundColor: defaultTheme.background.mediumGray,
      },
    }),
);

export const StyledCheckbox = styled.input(props =>
  mq({
    position: 'absolute',
    top: '0',
    left: '0',
    zIndex: 1,
    width: ['35px', '35px', '40px', '40px'],
    height: ['35px', '35px', '40px', '40px'],
    opacity: 0,
    cursor: props.disabled ? 'not-allowed' : 'pointer',
  }),
);

export const StyledLabel = styled.label(() =>
  mq({
    position: 'relative',
    display: 'block',
    width: ['35px', '35px', '40px', '40px'],
    height: ['35px', '35px', '40px', '40px'],
    margin: '0 auto',
  }),
);

const BetCheckBox: React.FC<BetCheckBoxProps> = ({
  checked,
  disabled,
  value,
  num,
  onChange,
}) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!disabled) onChange(e);
  };

  return (
    <StyledLabel>
      <NumberBox
        checked={checked}
        disabled={disabled}
        num={num}
        data-testid="number-box"
      />
      <StyledCheckbox
        type="checkbox"
        value={value}
        checked={checked}
        aria-checked={checked}
        disabled={disabled}
        aria-disabled={disabled}
        onChange={e => handleChange(e)}
      />
    </StyledLabel>
  );
};

export default BetCheckBox;
